/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useMemo, useState } from "react";
import { Col, Row } from "react-awesome-styled-grid";
import { Box, Text, theme } from "@nordcloud/gnui";
import { ErrorWrap } from "~/components/Error";
import { LoaderWrap } from "~/components/Loader";
import { TimelineCalendar, TimelineFiltersSidebar } from "./components";
import { DEFAULT_VIEW } from "./constants";
import { useGetTimelineEvents } from "./hooks";
import { TimelineFilterState } from "./types";
import {
  getDateRangeForView,
  mapIncomingEventsToCalendarEvents,
  mapPastEventsToCalendarEvents,
} from "./utils";

const EMPTY_FILTERS: TimelineFilterState = {
  planIds: [],
  actionTypes: [],
  actionIds: [],
  resourceGroupIds: [],
};

export function TimelinePage() {
  const [filters, setFilters] = useState<TimelineFilterState>(EMPTY_FILTERS);
  const [dateRange, setDateRange] = useState(() => {
    const { start, end } = getDateRangeForView(new Date(), DEFAULT_VIEW);
    return { start, end, view: DEFAULT_VIEW as string };
  });

  const { incomingEvents, pastEvents, loading, error } = useGetTimelineEvents({
    startDate: dateRange.start,
    endDate: dateRange.end,
    planIds: filters.planIds.length > 0 ? filters.planIds : undefined,
    actionIds: filters.actionIds.length > 0 ? filters.actionIds : undefined,
  });

  const calendarEvents = useMemo(
    () => [
      ...mapIncomingEventsToCalendarEvents(incomingEvents),
      ...mapPastEventsToCalendarEvents(pastEvents),
    ],
    [incomingEvents, pastEvents]
  );

  const handleDateRangeChange = useCallback(
    (start: Date, end: Date, view: string) => {
      setDateRange({
        start: start.toISOString(),
        end: end.toISOString(),
        view,
      });
    },
    []
  );

  const handleApplyFilters = (newFilters: TimelineFilterState) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
  };

  const handleFilterByPlan = useCallback((planId: string) => {
    setFilters((prev) => ({
      ...prev,
      planIds: [planId],
    }));
  }, []);

  return (
    <Row>
      <Col>
        <Box spacing="spacing04">
          <Row justify="between" align="center">
            <Col xs={4} sm={4} md={4} lg={6}>
              <Text
                tag="h1"
                size="lg"
                weight="bold"
                mb={0}
                data-testid="timeline-page-title"
              >
                Timeline
              </Text>
              <Text size="sm" color={theme.color.text.text02} mb={0}>
                Calendar view of all scheduled plans and actions
              </Text>
            </Col>
            <Col xs={4} sm={4} md={4} lg={6}>
              <div css={{ display: "flex", justifyContent: "flex-end" }}>
                <TimelineFiltersSidebar
                  filters={filters}
                  onApply={handleApplyFilters}
                  onClear={handleClearFilters}
                />
              </div>
            </Col>
          </Row>
        </Box>
        <Box spacing="spacing04">
          <ErrorWrap error={error}>
            <LoaderWrap loading={loading} inContent>
              <TimelineCalendar
                events={calendarEvents}
                onDateRangeChange={handleDateRangeChange}
                onFilterByPlan={handleFilterByPlan}
              />
            </LoaderWrap>
          </ErrorWrap>
        </Box>
      </Col>
    </Row>
  );
}
